/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Body, Button, buttonVariants, Title } from '@e2n/ui/v2';
import { Location } from '@/domain/mitarbeiter/features/rotationsplan/components/Location';
import { DeleteVorlageAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/features/DeleteVorlageAbschnitt/DeleteVorlageAbschnitt';
import { Markierung } from '@/domain/mitarbeiter/features/rotationsplan/entities/Markierung';
import type {
  VorlageAbschnittDetails,
  VorlageStationDetails,
} from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { getColorsForMarkierung } from '@/domain/mitarbeiter/features/rotationsplan/utils/getColorForMarkierung';
import { Mitarbeiter_RotationsplanVorlageStationTyp } from '@/gql/graphql';
import {
  faAlarmExclamation,
  faCalendarRange,
  faChevronDown,
  faChevronUp,
  faPen,
  faPlus,
  faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export function VorlageAbschnitt({
  vorlageId,
  abschnitt,
  istLehrjahr,
}: {
  vorlageId: string;
  abschnitt: VorlageAbschnittDetails;
  istLehrjahr: boolean;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-5">
      <div className="group flex items-center gap-2 p-1 md:hover:rounded-lg md:hover:bg-neutrals-v2-200">
        <Button
          iconOnly
          variant="ghost-on-grey"
          onClick={() => setIsOpen(!isOpen)}>
          <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
        </Button>
        <Title>{abschnitt.position + '. ' + abschnitt.name}</Title>
        <div className="ml-auto md:ml-0 md:hidden md:group-hover:block">
          <DeleteVorlageAbschnitt
            vorlageId={vorlageId}
            abschnitt={abschnitt}
            istLehrjahr={istLehrjahr}
          />
        </div>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-4">
          {abschnitt.stationen.map((station) => (
            <VorlageStationCard
              key={station.id}
              station={station}
              updatePath={`/mitarbeiter/rotationsplaene/vorlagen/${vorlageId}/${abschnitt.id}/station/${station.id}/update`}
            />
          ))}
          <Link
            to={`/mitarbeiter/rotationsplaene/vorlagen/${vorlageId}/${abschnitt.id}/station/create`}
            className="group/create flex items-center gap-2 w-full p-4 bg-backgrounds-primary rounded-xl">
            <span
              className={buttonVariants({
                variant: 'secondary-red',
                iconOnly: true,
                className: 'group-hover/create:bg-neutrals-v2-300',
              })}>
              <FontAwesomeIcon icon={faPlus} />
            </span>
            <Body>
              {t('domain.mitarbeiter.rotationsplan.vorlagen.details.erstellen')}
            </Body>
          </Link>
        </div>
      )}
    </div>
  );
}

function VorlageStationCard({
  station,
  updatePath,
}: {
  station: VorlageStationDetails;
  updatePath: string;
}) {
  const { t } = useTranslation();
  const istZeitraumOhneStation =
    station.typ ===
    Mitarbeiter_RotationsplanVorlageStationTyp.ZeitraumOhneStation;

  return (
    <div className="w-full min-w-0 bg-backgrounds-primary rounded-xl flex flex-col gap-3 p-3.5">
      <div className="flex justify-between items-start gap-8">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center gap-2">
            {station.arbeitsbereich && (
              <FontAwesomeIcon
                icon={faCircle}
                color={
                  getColorsForMarkierung(
                    station.arbeitsbereich.markierung as unknown as Markierung,
                  ).base
                }
                className="size-2.5"
              />
            )}
            <Title>
              {istZeitraumOhneStation
                ? t(
                    'domain.mitarbeiter.rotationsplan.vorlagen.details.zeitraumOhneStation',
                  )
                : station.arbeitsbereich?.name}
            </Title>
          </div>
          {station.lernziele.length > 0 && (
            <Body className="text-labels-secondary line-clamp-3">
              {station.lernziele}
            </Body>
          )}
        </div>
        <Button asChild className="shrink-0" iconOnly variant="secondary-red">
          <Link aria-label={t('actions.bearbeiten')} to={updatePath}>
            <FontAwesomeIcon icon={faPen} />
          </Link>
        </Button>
      </div>
      <div className="flex flex-col items-start gap-1 text-labels-secondary md:flex-row md:flex-wrap md:items-center md:gap-x-3">
        <div className="flex items-center gap-1">
          <FontAwesomeIcon className="size-3.5" icon={faCalendarRange} />
          <Body className="text-labels-secondary" size="sm">
            {t(
              `domain.mitarbeiter.rotationsplan.vorlagen.details.dauer.${station.dauerEinheit}`,
              { count: station.dauer },
            )}
          </Body>
        </div>
        {station.abteilungsleiter && (
          <div className="flex items-center gap-1">
            <FontAwesomeIcon className="size-3.5" icon={faUser} />
            <Body className="text-labels-secondary" size="sm">
              {`${station.abteilungsleiter.vorname} ${station.abteilungsleiter.nachname}`}
            </Body>
          </div>
        )}
        {station.standort && (
          <Location
            className="text-labels-secondary"
            standort={station.standort}
          />
        )}
        {station.erinnerungszeiten.length > 0 && (
          <div className="flex max-w-full min-w-0 items-center gap-1 text-xs">
            <FontAwesomeIcon icon={faAlarmExclamation} />
            <span className="whitespace-nowrap truncate">
              {station.erinnerungszeiten
                .map((zeit) =>
                  t(
                    `domain.mitarbeiter.fristen.erinnerungsZeit.options.${zeit}`,
                  ),
                )
                .join(', ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
